import React from 'react';
import { cx } from '../shared/ui/utils';

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const FiltersPanel = ({
  filters,
  destinations = [],
  themes = [],
  facets,
  onChange,
  onReset,
  labels = {},
  compact = false,
  visibility = {},
}) => {
  const priceRange = facets?.priceRange || { min: 0, max: 100000 };
  const durationRange = facets?.durationRange || { min: 1, max: 21 };
  const destinationCounts = new Map((facets?.destinations || []).map((row) => [row.slug, row.count]));
  const themeCounts = new Map((facets?.themes || []).map((row) => [row.slug, row.count]));

  const updateArray = (key, value) => {
    const current = filters[key] || [];
    const exists = current.includes(value);
    const next = exists ? current.filter((item) => item !== value) : [...current, value];
    onChange({ [key]: next });
  };

  const minPrice = toNumber(filters.budgetMin) ?? priceRange.min;
  const maxPrice = toNumber(filters.budgetMax) ?? priceRange.max;
  const minDuration = toNumber(filters.durationMin) ?? durationRange.min;
  const maxDuration = toNumber(filters.durationMax) ?? durationRange.max;

  const handlePriceMin = (value) => {
    const next = Math.min(Number(value), maxPrice);
    onChange({ budgetMin: value === '' ? '' : String(next) });
  };

  const handlePriceMax = (value) => {
    const next = Math.max(Number(value), minPrice);
    onChange({ budgetMax: value === '' ? '' : String(next) });
  };

  const handleDurationMin = (value) => {
    const next = Math.min(Number(value), maxDuration);
    onChange({ durationMin: value === '' ? '' : String(next) });
  };

  const handleDurationMax = (value) => {
    const next = Math.max(Number(value), minDuration);
    onChange({ durationMax: value === '' ? '' : String(next) });
  };

  return (
    <div className={cx('space-y-6', compact && 'text-sm')}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{labels.filtersLabel || 'Filters'}</p>
      </div>
      {visibility.search !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Search</label>
        <input
          className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="Package title"
          value={filters.search}
          onChange={(e) => onChange({ search: e.target.value })}
        />
      </div>
      ) : null}
      {visibility.destination !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Destination</label>
        <div className="mt-2 max-h-48 space-y-2 overflow-auto rounded-2xl border border-slate-200 bg-white p-3">
          {destinations.map((dest) => {
            const value = dest.slug || dest.name;
            const count = destinationCounts.get(dest.slug || dest.name) ?? dest.trip_count;
            return (
              <label key={dest.id || value} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.destination.includes(value)}
                    onChange={() => updateArray('destination', value)}
                  />
                  {dest.name}
                </span>
                {count !== undefined ? <span className="text-xs text-slate-400">{count}</span> : null}
              </label>
            );
          })}
        </div>
      </div>
      ) : null}
      {visibility.budget !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{labels.budget || 'Budget'}</label>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Min"
              value={filters.budgetMin}
              onChange={(e) => handlePriceMin(e.target.value)}
            />
            <input
              type="number"
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Max"
              value={filters.budgetMax}
              onChange={(e) => handlePriceMax(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <input
              type="range"
              min={priceRange.min}
              max={priceRange.max}
              value={minPrice}
              onChange={(e) => handlePriceMin(e.target.value)}
              className="w-full"
            />
            <input
              type="range"
              min={priceRange.min}
              max={priceRange.max}
              value={maxPrice}
              onChange={(e) => handlePriceMax(e.target.value)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>{priceRange.min}</span>
              <span>{priceRange.max}</span>
            </div>
          </div>
        </div>
      </div>
      ) : null}
      {visibility.duration !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Duration (days)</label>
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Min"
              value={filters.durationMin}
              onChange={(e) => handleDurationMin(e.target.value)}
            />
            <input
              type="number"
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
              placeholder="Max"
              value={filters.durationMax}
              onChange={(e) => handleDurationMax(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <input
              type="range"
              min={durationRange.min}
              max={durationRange.max}
              value={minDuration}
              onChange={(e) => handleDurationMin(e.target.value)}
              className="w-full"
            />
            <input
              type="range"
              min={durationRange.min}
              max={durationRange.max}
              value={maxDuration}
              onChange={(e) => handleDurationMax(e.target.value)}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-400">
              <span>{durationRange.min}d</span>
              <span>{durationRange.max}d</span>
            </div>
          </div>
        </div>
      </div>
      ) : null}
      {visibility.rating !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Rating</label>
        <select
          value={filters.ratingMin}
          onChange={(e) => onChange({ ratingMin: e.target.value })}
          className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
        >
          <option value="">All</option>
          <option value="3.5">3.5+</option>
          <option value="4">4.0+</option>
          <option value="4.5">4.5+</option>
          <option value="4.8">4.8+</option>
        </select>
      </div>
      ) : null}
      {visibility.theme !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Themes</label>
        <div className="mt-2 max-h-48 space-y-2 overflow-auto rounded-2xl border border-slate-200 bg-white p-3">
          {themes.map((theme) => {
            const value = theme.slug || String(theme.id);
            const count = themeCounts.get(theme.slug || theme.name);
            return (
              <label key={theme.id || value} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.themes.includes(value)}
                    onChange={() => updateArray('themes', value)}
                  />
                  {theme.name}
                </span>
                {count !== undefined ? <span className="text-xs text-slate-400">{count}</span> : null}
              </label>
            );
          })}
        </div>
      </div>
      ) : null}
      {visibility.travellers !== false ? (
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{labels.travellers || 'Travellers'}</label>
        <input
          type="number"
          min="1"
          className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          placeholder="2"
          value={filters.travellers}
          onChange={(e) => onChange({ travellers: e.target.value })}
        />
      </div>
      ) : null}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={filters.featured}
            onChange={(e) => onChange({ featured: e.target.checked })}
          />
          Featured
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={filters.trending}
            onChange={(e) => onChange({ trending: e.target.checked })}
          />
          Trending
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={filters.topRated}
            onChange={(e) => onChange({ topRated: e.target.checked })}
          />
          Top rated
        </label>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="w-full rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em]"
      >
        {labels.clearFilters || 'Clear filters'}
      </button>
    </div>
  );
};

export default FiltersPanel;
